import { LoadingButton } from '@mui/lab';
import { Alert, Card, CardContent, Collapse, Grid, TextField } from '@mui/material';
import clsx from 'clsx';
import { isString } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../constants/routes';
import { useEdit, useRouter } from '../../../hooks';
import {
  clearApplication,
  clearApplicationRequest,
  createApplication,
  editApplication,
  getApplication,
} from '../../../thunks/application-thunks';
import { DirectionsSelect } from '../../directions';
import { DistrictsSelect } from '../../districts';
import { Page } from '../../page';
import { RegionsSelect } from '../../regions';
import {
  ApplicationEditValidationForm,
  FIELD_ADDRESS,
  FIELD_DIRECTION,
  FIELD_DISTRICT,
  FIELD_EMAIL,
  FIELD_FIRST_NAME,
  FIELD_LAST_NAME,
  FIELD_MIDDLE_NAME,
  FIELD_PHONE,
  FIELD_PIN,
  FIELD_REGION,
} from './applications-edit-schema';

interface ApplicationsEditFormProps {
  className?: string;
  id?: string | number;
}

export const ApplicationsEditForm: ComponentType<ApplicationsEditFormProps> = ({
  className,
  id,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isLoading, isFetching, error, formik, handleFieldChange, hasError, getErrorMessage } =
    useEdit({
      id,
      entity: 'application',
      initialValues: {
        [FIELD_FIRST_NAME]: '',
        [FIELD_LAST_NAME]: '',
        [FIELD_MIDDLE_NAME]: '',
        [FIELD_PIN]: '',
        [FIELD_ADDRESS]: '',
        [FIELD_PHONE]: '',
        [FIELD_EMAIL]: '',
        [FIELD_DIRECTION]: '',
        [FIELD_REGION]: '',
        [FIELD_DISTRICT]: '',
      },
      validationSchema: ApplicationEditValidationForm,
      onGetOne: getApplication,
      onClearOne: clearApplication,
      onClear: clearApplicationRequest,
      onCreate: createApplication,
      onEdit: editApplication,
      onSubmit: () => router.push(ROUTES.APPLICATIONS.ROOT),
    });

  const inputProps = (field: string) => ({
    id: `create-${field}`,
    error: hasError(field),
    helperText: getErrorMessage(field),
    disabled: isLoading || isFetching,
    fullWidth: true,
    size: 'small',
    InputProps: {
      autoComplete: 'off',
      disabled: isLoading || isFetching,
    },
  });

  return (
    <Page title={t('applications.create', 'Создать заявку на аттестацию')}>
      <Card>
        <CardContent>
          <form onSubmit={formik.handleSubmit} className={clsx(className)}>
            <Grid container direction="column" spacing={1} mb={1}>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_FIRST_NAME)}
                  {...formik.getFieldProps(FIELD_FIRST_NAME)}
                  onChange={(e: any) => handleFieldChange(FIELD_FIRST_NAME, e)}
                  label={t('profile.firstName', 'Имя')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_LAST_NAME)}
                  {...formik.getFieldProps(FIELD_LAST_NAME)}
                  onChange={(e: any) => handleFieldChange(FIELD_LAST_NAME, e)}
                  label={t('profile.lastName', 'Фамилия')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_MIDDLE_NAME)}
                  {...formik.getFieldProps(FIELD_MIDDLE_NAME)}
                  onChange={(e: any) => handleFieldChange(FIELD_MIDDLE_NAME, e)}
                  label={t('profile.middleName', 'Отчество')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_PIN)}
                  {...formik.getFieldProps(FIELD_PIN)}
                  onChange={(e: any) => handleFieldChange(FIELD_PIN, e)}
                  label={t('profile.pin', 'ПИНФЛ')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_PHONE)}
                  {...formik.getFieldProps(FIELD_PHONE)}
                  onChange={(e: any) => handleFieldChange(FIELD_PHONE, e)}
                  label={t('profile.phone', 'Телефон')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_EMAIL)}
                  {...formik.getFieldProps(FIELD_EMAIL)}
                  onChange={(e: any) => handleFieldChange(FIELD_EMAIL, e)}
                  label={t('profile.email', 'Email')}
                />
              </Grid>
              {/*<Grid item>*/}
              {/*  <TextField*/}
              {/*    {...inputProps(FIELD_PEV_UID)}*/}
              {/*    {...formik.getFieldProps(FIELD_PEV_UID)}*/}
              {/*    onChange={(e: any) => handleFieldChange(FIELD_PEV_UID, e)}*/}
              {/*    label="Пред. UID"*/}
              {/*  />*/}
              {/*</Grid>*/}
              <Grid item>
                <DirectionsSelect
                  {...inputProps(FIELD_DIRECTION)}
                  {...formik.getFieldProps(FIELD_DIRECTION)}
                  onChange={(e: any) => handleFieldChange(FIELD_DIRECTION, e)}
                  label={t('direction', 'Направление')}
                />
              </Grid>
              <Grid item>
                <RegionsSelect
                  {...inputProps(FIELD_REGION)}
                  {...formik.getFieldProps(FIELD_REGION)}
                  onChange={(e: any) => handleFieldChange(FIELD_REGION, e)}
                  label={t('region', 'Регион')}
                />
              </Grid>
              <Grid item>
                <DistrictsSelect
                  {...inputProps(FIELD_DISTRICT)}
                  {...formik.getFieldProps(FIELD_DISTRICT)}
                  onChange={(e: any) => handleFieldChange(FIELD_DISTRICT, e)}
                  label={t('district', 'Район')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_ADDRESS)}
                  {...formik.getFieldProps(FIELD_ADDRESS)}
                  onChange={(e: any) => handleFieldChange(FIELD_ADDRESS, e)}
                  label={t('location.address.label', 'Адрес')}
                />
              </Grid>
            </Grid>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={isLoading}
              fullWidth
              sx={{ marginBottom: error ? 1 : 0 }}
            >
              {t('action.create', 'Создать')}
            </LoadingButton>

            {isString(error) && (
              <Collapse in={!!error}>
                <Alert severity="error">{error}</Alert>
              </Collapse>
            )}
          </form>
        </CardContent>
      </Card>
    </Page>
  );
};
